import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, LoaderCircle, Plus } from 'lucide-react';
import { useCreateOrganization } from '@/resources/organization/organization.service';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ErrorNote, SectionHeader } from './ui';

export function CreateOrganizationSection({
  first,
  onCreated,
}: {
  first: boolean;
  onCreated: (organizationId: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const create = useCreateOrganization();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate(
      { name },
      {
        onSuccess: (res) => {
          setName('');
          onCreated(res.organization.id);
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader
        title={first ? t('organization.welcomeTitle') : t('organization.createTitle')}
        hint={first ? t('organization.welcomeHint') : t('organization.createHint')}
      />

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="size-4" />
            {t('organization.cardTitle')}
          </CardTitle>
          <CardDescription>{t('organization.cardHint')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="org-name">{t('organization.nameLabel')}</FieldLabel>
                <Input
                  id="org-name"
                  required
                  minLength={2}
                  maxLength={60}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('organization.namePlaceholder')}
                />
                <FieldDescription>{t('organization.nameHint')}</FieldDescription>
              </Field>
              <Field>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? (
                    <LoaderCircle className="size-4 animate-spin" />
                  ) : (
                    <Plus />
                  )}
                  {create.isPending
                    ? t('organization.creating')
                    : t('organization.create')}
                </Button>
              </Field>
              <ErrorNote error={create.error} />
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
